import { Link } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { cn } from '@/lib/utils';

type AvatarMenuProps = {
  className?: string;
};

function deriveInitials(email: string): string {
  const localPart = email.split('@')[0];
  if (!localPart) return '?';
  const segments = localPart.split(/[.-]/);
  if (segments.length >= 2 && segments[0] && segments[1]) {
    return (segments[0][0] + segments[1][0]).toUpperCase();
  }
  if (localPart.length >= 1) {
    return localPart.slice(0, 2).toUpperCase();
  }
  return '?';
}

export function AvatarMenu({ className }: AvatarMenuProps) {
  const { logout, tokenService } = useAuth();
  const { t, language, setLanguage, availableLanguages } = useTranslation();

  const email = tokenService?.getUserMail() ?? '';
  const initials = deriveInitials(email);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="avatar-menu-trigger"
          aria-label={t('avatarMenu.ariaLabel')}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-sm font-semibold text-white transition hover:bg-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
            className
          )}
        >
          {initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="avatar-menu-dropdown">
        <DropdownMenuLabel className="text-muted-foreground font-normal">{email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t('avatarMenu.language')}
        </DropdownMenuLabel>
        {/* Picking a language keeps the menu open (the item's select is cancelled). */}
        <DropdownMenuItem asChild onSelect={(event) => event.preventDefault()}>
          <div className="flex gap-2 px-2 py-1">
            <SegmentedControl
              size="sm"
              aria-label={t('avatarMenu.language')}
              value={language}
              onChange={setLanguage}
              options={availableLanguages.map((lang) => ({
                value: lang.value,
                label: (
                  <>
                    <span aria-hidden>{lang.value.toUpperCase()}</span>
                    <span className="sr-only">{lang.label}</span>
                  </>
                ),
                'data-testid': `lang-btn-${lang.value}`,
              }))}
            />
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile">{t('avatarMenu.profile')}</Link>
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="logout-item" onClick={logout}>
          {t('avatarMenu.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
